import { enumMembers } from "../apiSchema";
import { requestJson, withQuery } from "../http/httpClient";
import {
  isJsonObject,
  readArray,
  readInteger,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  readStringRecord,
  type JsonObject,
} from "../http/jsonDecoding";
import { MODEL_USES } from "./authoring/stepCatalogueApi";
import { decodeStepDefinition, type StepDefinition, type ToolStepDefinition } from "./authoring/stepDecoders";
import { decodeWorkflowStep } from "./authoring/processContentDecoders";
import { VALUE_KINDS, decodeInputSlot, decodeRecord, oneOf, type ItemSelector, type PlaceholderSource, type Selector } from "./authoring/selectorDecoders";
import {
  workflowStepView,
  type ArtifactSummary,
  type ItemTarget,
  type NamedItem,
  type OutputPartReference,
  type SelectorRead,
  type StepKind,
  type StepPorts,
  type StepSummary,
  type ToolSummary,
  type WorkflowDefinition,
} from "./processDefinitions";

/**
 * Reads process items for the Workflow view from `GET /content/items/{version ref}`
 * (module B). A Workflow, a step, a tool or an artifact becomes the summary the view draws
 * (`processDefinitions.ts`); every other kind is `other`. Both `dmw-workflow/1` and `/2`
 * and both `dmw-step/1` and `/2` are read: `/1` has no roles, sub-workflows, exports or
 * tool steps (processes.md P-1, P-13).
 */

export type ProcessItem =
  | { readonly kind: "workflow"; readonly workflow: WorkflowDefinition }
  | { readonly kind: "step"; readonly step: StepSummary }
  | { readonly kind: "tool"; readonly tool: ToolSummary }
  | { readonly kind: "artifact"; readonly artifact: ArtifactSummary }
  | { readonly kind: "other"; readonly item: NamedItem };

export const STEP_KINDS = enumMembers<StepKind>({ agent: true, human: true, tool: true, talk: true });

export function readProcessItem(reference: string, signal?: AbortSignal): Promise<ProcessItem> {
  return requestJson({
    method: "GET",
    path: itemPath(reference),
    signal,
    decodeResponse: decodeProcessItem,
  });
}

/** An item as the project's listing names it. */
export interface ListedItem {
  readonly kind: string;
  readonly reference: string;
  readonly name: string;
}

/** The schema of a project's bindings artifact (process-governance.md PG-5). */
const BINDINGS_SCHEMA = "dmw-bindings/1";
/** Artifacts read at once while looking for the bindings. */
const BINDINGS_READ_BATCH = 8;

/**
 * Which item fills each role in the project (role name → item ID), from its bindings
 * artifact: at most one per project. No route names that artifact, so this reads the
 * given artifacts (`VersionRef`s, from the project's listing) a batch at a time until it
 * finds it; empty if the project has none.
 */
export async function readRoleBindings(artifacts: readonly string[], signal?: AbortSignal): Promise<RoleBindings> {
  for (let start = 0; start < artifacts.length; start += BINDINGS_READ_BATCH) {
    const batch = artifacts.slice(start, start + BINDINGS_READ_BATCH);
    const roles = await Promise.all(
      batch.map((reference) => requestJson({ method: "GET", path: itemPath(reference), signal, decodeResponse: decodeRoleBindings })),
    );
    const index = roles.findIndex((candidate) => candidate !== null);
    const found = roles[index];
    const reference = batch[index];
    if (found !== undefined && found !== null && reference !== undefined) {
      return { reference, roles: found };
    }
  }
  return NO_ROLE_BINDINGS;
}

/** The project's bindings: the artifact version that holds them, and role name → item ID. */
export interface RoleBindings {
  /** `VersionRef` of the bindings artifact; `null` when the project has none. */
  readonly reference: string | null;
  readonly roles: ReadonlyMap<string, string>;
}

export const NO_ROLE_BINDINGS: RoleBindings = { reference: null, roles: new Map() };

/** The roles of a bindings artifact; `null` for any other item. */
function decodeRoleBindings(body: unknown): ReadonlyMap<string, string> | null {
  const definition = readObject(body, "item version")["definition"];
  if (!isJsonObject(definition) || definition["schema"] !== BINDINGS_SCHEMA || !isJsonObject(definition["content"])) {
    return null;
  }
  const roles = definition["content"]["roles"];
  if (!isJsonObject(roles)) {
    return new Map();
  }
  return new Map(Object.entries(roles).flatMap(([role, item]) => (typeof item === "string" ? [[role, item] as const] : [])));
}

/** Every item of the project's content version `commit` (`null`: the latest), by item ID: the whole listing, page by page. */
export async function readItemListing(projectId: string, signal?: AbortSignal, commit: string | null = null): Promise<ReadonlyMap<string, ListedItem>> {
  const items = new Map<string, ListedItem>();
  await readListing(projectId, commit, signal, (itemId, item) => {
    items.set(itemId, item);
    return true;
  });
  return items;
}

/** Calls `visit` for each listed item, page by page, until it returns `false` or the listing ends. */
async function readListing(projectId: string, commit: string | null, signal: AbortSignal | undefined, visit: (itemId: string, item: ListedItem) => boolean): Promise<void> {
  let offset = 0;
  for (;;) {
    const page = await requestJson({
      method: "GET",
      path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/items`, { version: commit, offset }),
      signal,
      decodeResponse: (body) => {
        const listing = readObject(body, "item listing");
        return {
          total: readInteger(listing, "total_item_count"),
          items: readArray(listing["items"], "items", (item) => {
            const entry = readObject(item, "item");
            return { itemId: readString(entry, "item_id"), item: { kind: readString(entry, "kind"), reference: readString(entry, "reference"), name: readString(entry, "name") } };
          }),
        };
      },
    });
    for (const { itemId, item } of page.items) {
      if (!visit(itemId, item)) {
        return;
      }
    }
    offset += page.items.length;
    if (page.items.length === 0 || offset >= page.total) {
      return;
    }
  }
}

function itemPath(reference: string): `/${string}` {
  return `/content/items/${encodeURIComponent(reference).replaceAll("%40", "@")}`;
}

export function decodeProcessItem(body: unknown): ProcessItem {
  const item = readObject(body, "item version");
  const reference = readString(item, "reference");
  const name = readString(item, "name");
  const kind = readString(item, "kind");
  const definition = isJsonObject(item["definition"]) ? item["definition"] : null;
  if (kind === "workflow" && definition !== null) {
    // Its name and description in the person's language, as the server gives them beside its own words (P-21).
    const shown = { name, description: typeof item["description"] === "string" ? item["description"] : readString(definition, "description") };
    return { kind: "workflow", workflow: decodeWorkflow(reference, definition, shown) };
  }
  if (kind === "step" && definition !== null) {
    const step = stepSummaryOf(reference, decodeStepDefinition(definition["definition"]), decodeStepPorts(item["ports"]));
    return { kind: "step", step: { ...step, name, description: typeof item["description"] === "string" ? item["description"] : step.description, choiceLabels: item["choice_labels"] == null ? null : readStringRecord(item, "choice_labels"), instructions: typeof item["instructions"] === "string" ? item["instructions"] : null } };
  }
  if (kind === "tool" && definition !== null) {
    return { kind: "tool", tool: decodeTool(reference, definition) };
  }
  if (kind === "artifact") {
    return { kind: "artifact", artifact: { reference, name, madeBy: decodeMadeBy(item["made_by"]) } };
  }
  const labels = kind === "label_list" && definition !== null ? definition["labels"] : undefined;
  return { kind: "other", item: { reference, name, kind, labelCount: Array.isArray(labels) ? labels.length : null } };
}

function decodeWorkflow(reference: string, content: JsonObject, shown: { readonly name: string; readonly description: string }): WorkflowDefinition {
  return {
    reference,
    name: shown.name,
    description: shown.description,
    steps: readArray(content["steps"], "Workflow steps", (value) => workflowStepView(decodeWorkflowStep(value))),
    exports: decodeBindings(content["exports"], "exports"),
  };
}

function decodeBindings(value: unknown, context: string): Readonly<Record<string, OutputPartReference>> {
  if (value === undefined) {
    return {};
  }
  const bindings = readObject(value, context);
  return Object.fromEntries(
    Object.entries(bindings).map(([name, binding]) => {
      const part = readObject(binding, `${context} ${name}`);
      return [name, { fromStep: readString(part, "from_step"), part: readString(part, "part") }];
    }),
  );
}

/** A step version's ports as module B serves them with the version (W-6); `null` where none were served. */
function decodeStepPorts(value: unknown): StepPorts | null {
  if (value === undefined || value === null) {
    return null;
  }
  const ports = readObject(value, "step ports");
  const outputs = ports["outputs"];
  return {
    inputs: decodeRecord(ports["inputs"], "input slots", decodeInputSlot),
    outputs: outputs === null ? null : decodeRecord(outputs, "output parts", (kinds) => readArray(kinds, "part kinds", (kind) => oneOf(kind, VALUE_KINDS, "value kind"))),
  };
}

/** A step version as the Workflow view reads it, from its whole definition and the ports module B serves with it. */
export function stepSummaryOf(reference: string, definition: StepDefinition, ports: StepPorts | null): StepSummary {
  // Agent steps fill placeholders; tool steps fill tool inputs. Both use selector sources.
  const sources = definition.kind === "agent" ? definition.placeholders : definition.kind === "tool" ? definition.inputs : {};
  return {
    reference,
    name: definition.name,
    description: definition.description,
    kind: definition.kind,
    tool: definition.kind === "tool" ? definition.tool : null,
    selectorReads: selectorReadsOf(sources),
    proposes: definition.kind === "tool" ? proposedTarget(definition.propose) : null,
    definition,
    ports,
  };
}

/** Every selector source, with the items it names (floating `{item}`, pinned `{version}`, or `{role}`). */
function selectorReadsOf(sources: Readonly<Record<string, PlaceholderSource>>): readonly SelectorRead[] {
  return Object.entries(sources).flatMap(([input, source]) => (source.source === "selector" ? [{ input, selector: source.selector, targets: selectorTargets(source.selector) }] : []));
}

/** The items a selector names, in its own order. */
export function selectorTargets(selector: Selector): readonly ItemTarget[] {
  switch (selector.kind) {
    case "label_list":
      return [itemTargetOf(selector.label_list)];
    case "item":
      return [itemTargetOf(selector.item)];
    case "ontology_terms":
      return [...selector.modules.map(itemTargetOf), ...(selector.annotation_properties_from == null ? [] : [itemTargetOf(selector.annotation_properties_from)])];
    case "few_shot_examples":
      return selector.examples.source === "items" ? selector.examples.items.map(itemTargetOf) : [];
    case "earlier_results":
    case "accepted_instance_data":
    case "reference_vocabularies":
    case "derived_records":
    case "subject_text":
    case "competency_questions":
    case "source_scope":
    case "source_version":
      return [];
  }
}

/** The item a tool step's `propose.as.item` names, if it proposes a version of one. */
function proposedTarget(propose: ToolStepDefinition["propose"]): ItemTarget | null {
  const as = propose?.as;
  return as !== undefined && "item" in as && as.item !== "new" ? itemTargetOf(as.item) : null;
}

function itemTargetOf(selector: ItemSelector): ItemTarget {
  if ("item" in selector) {
    return { by: "item", item: selector.item };
  }
  return "role" in selector ? { by: "role", role: selector.role } : { by: "version", version: selector.version };
}

/** A tool item: its outputs and whether it loads a model. */
function decodeTool(reference: string, content: JsonObject): ToolSummary {
  const outputs = isJsonObject(content["outputs"]) ? content["outputs"] : {};
  return {
    reference,
    name: readString(content, "name"),
    outputs: Object.fromEntries(Object.entries(outputs).map(([name, output]) => [name, readOneOf(readObject(output, `tool output ${name}`), "kind", VALUE_KINDS)])),
    modelUse: readOneOf(content, "model_use", MODEL_USES),
  };
}

/** A version merged from a change set that names no Workflow (seeded, or edited by hand) has no producer. */
function decodeMadeBy(value: unknown): ArtifactSummary["madeBy"] {
  if (value === undefined || value === null) {
    return null;
  }
  const madeBy = readObject(value, "made by");
  const workflow = readNullableString(madeBy, "workflow");
  return workflow === null ? null : { workflow, thread: readNullableString(madeBy, "thread") };
}

/** How many items of each kind the project's latest content version holds. */
export async function countItemsByKind(projectId: string, signal?: AbortSignal): Promise<ReadonlyMap<string, number>> {
  const counts = new Map<string, number>();
  await readListing(projectId, null, signal, (_itemId, item) => {
    counts.set(item.kind, (counts.get(item.kind) ?? 0) + 1);
    return true;
  });
  return counts;
}
